import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

export type FgaTypeDefinition = {
  type: string;
  relations: Record<string, any>;
  metadata?: any;
};

export type FgaModel = {
  id: string;
  schema_version: string;
  type_definitions: FgaTypeDefinition[];
};

export type ModelResponse = {
  storeId: string;
  modelId: string | null;
  model: FgaModel | null;
};

export type CheckRequest = {
  user: string;
  relation: string;
  object: string;
};

export type CheckResponse = {
  allowed: boolean;
  latencyMs: number;
};

export type StatusMode = 'rbac' | 'shadow' | 'enforce';

export type GrantRelation = 'owner' | 'admin' | 'editor' | 'viewer' | 'executor' | 'superadmin';
export type GrantOrigin = 'direct' | 'via_group' | 'via_tenant';

export interface Grant {
  user: string;
  relation: GrantRelation;
  origin: GrantOrigin;
  groupName?: string;
  tenantName?: string;
  /** Present when the tuple has a non_expired_grant condition. */
  grantTime?: string;
  grantDuration?: string;
}

export interface GrantsResponse {
  can_manage: boolean;
  can_delete: boolean;
  can_view: boolean;
  grants: Grant[];
}

export interface GrantRequest {
  target: string;
  relation: GrantRelation;
  object: string;
  /** ISO 8601 duration (e.g. "168h"). When present, writes a conditional tuple. */
  grantDuration?: string;
}

export interface RevokeRequest {
  target: string;
  relation: GrantRelation;
}

export type StatusResponse = {
  mode: StatusMode;
  openfgaConfigured: boolean;
  apiUrl: string | null;
  storeId: string | null;
  modelId: string | null;
  enforce: {
    catalog?: boolean;
    scaffolder?: boolean;
    kubernetes?: boolean;
  };
};

export class RebacExplorerClient {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(opts: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = opts.discoveryApi;
    this.fetchApi = opts.fetchApi;
  }

  private async baseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('rebac-explorer');
  }

  async getModel(): Promise<ModelResponse> {
    const base = await this.baseUrl();
    const resp = await this.fetchApi.fetch(`${base}/model`);
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error((err as any).error ?? `HTTP ${resp.status}`);
    }
    return resp.json();
  }

  async getObjects(opts: { type: string; q?: string; limit?: number }): Promise<string[]> {
    const base = await this.baseUrl();
    const params = new URLSearchParams({ type: opts.type });
    if (opts.q) params.set('q', opts.q);
    if (opts.limit) params.set('limit', String(opts.limit));
    const resp = await this.fetchApi.fetch(`${base}/objects?${params}`);
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error((err as any).error ?? `HTTP ${resp.status}`);
    }
    const data = await resp.json();
    return data.objects ?? [];
  }

  async getStatus(): Promise<StatusResponse> {
    const base = await this.baseUrl();
    const resp = await this.fetchApi.fetch(`${base}/status`);
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error((err as any).error ?? `HTTP ${resp.status}`);
    }
    return resp.json();
  }

  async getGrants(resourceType: string, resourceId: string): Promise<GrantsResponse> {
    const base = await this.baseUrl();
    const resp = await this.fetchApi.fetch(
      `${base}/permissions/${encodeURIComponent(resourceType)}/${encodeURIComponent(resourceId)}/delegations`,
    );
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error((err as any).error ?? `HTTP ${resp.status}`);
    }
    return resp.json();
  }

  async grantAccess(req: GrantRequest): Promise<void> {
    const base = await this.baseUrl();
    const [resourceType, resourceId] = req.object.split(':');
    const resp = await this.fetchApi.fetch(
      `${base}/permissions/${encodeURIComponent(resourceType)}/${encodeURIComponent(resourceId)}/delegations`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role: req.relation,
          targetUserId: req.target,
          ...(req.grantDuration ? { grantDuration: req.grantDuration } : {}),
        }),
      },
    );
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error((err as any).error ?? `HTTP ${resp.status}`);
    }
  }

  async revokeAccess(resourceType: string, resourceId: string, req: RevokeRequest): Promise<void> {
    const base = await this.baseUrl();
    const encodedTarget = encodeURIComponent(req.target);
    const resp = await this.fetchApi.fetch(
      `${base}/permissions/${encodeURIComponent(resourceType)}/${encodeURIComponent(resourceId)}/delegations/${encodedTarget}?relation=${encodeURIComponent(req.relation)}`,
      { method: 'DELETE' },
    );
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error((err as any).error ?? `HTTP ${resp.status}`);
    }
  }

  async getRelationsForType(resourceType: string, resourceId?: string): Promise<GrantRelation[]> {
    // Plugins que só expõem um subconjunto de relações na UI de concessão.
    // Evita oferecer viewer/editor em plugins onde essas relações não têm efeito.
    const PLUGIN_RELATION_OVERRIDES: Partial<Record<string, GrantRelation[]>> = {
      devtools: ['admin'],
    };
    if (resourceType === 'plugin' && resourceId && PLUGIN_RELATION_OVERRIDES[resourceId]) {
      return PLUGIN_RELATION_OVERRIDES[resourceId]!;
    }

    const { model } = await this.getModel();
    if (!model) return ['viewer', 'editor', 'admin'];

    const typeDef = model.type_definitions.find(td => td.type === resourceType);
    if (!typeDef) return ['viewer', 'editor', 'admin'];

    const EXCLUDED = new Set(['tenant', 'owner', 'parent', 'member']);
    const isComputed = (r: string) => r.startsWith('can_');

    return Object.keys(typeDef.relations).filter(
      r => !isComputed(r) && !EXCLUDED.has(r),
    ) as GrantRelation[];
  }

  async check(req: CheckRequest): Promise<CheckResponse> {
    const base = await this.baseUrl();
    const resp = await this.fetchApi.fetch(`${base}/check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error((err as any).error ?? `HTTP ${resp.status}`);
    }
    return resp.json();
  }
}
